import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, formatApiErrorDetail, SERVICE_STATUS_LABELS } from "@/lib/api";
import { Input } from "@/components/ui/input";
import PublicAIChatWidget from "@/components/public/PublicAIChatWidget";
import LanguageSwitcher from "@/components/public/LanguageSwitcher";
import { useLang } from "@/lib/i18n";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Search,
  CalendarClock,
  MapPin,
  Plane,
  Users as UsersIcon,
  Phone,
  ArrowLeft,
  CreditCard,
  CheckCircle2,
  Send,
} from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_complaint-tracker-102/artifacts/5d2kmds0_IMG_2B71FDB9-74FD-4451-B622-B7FCD5B5DCBD.jpeg";

export default function ClientLookupPage() {
  const { t } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [booking, setBooking] = useState(searchParams.get("b") || "");
  const [phone, setPhone] = useState(searchParams.get("p") || "");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [payMsg, setPayMsg] = useState("");

  const submit = async (e) => {
    if (e) e.preventDefault();
    setErr("");
    setData(null);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/public/service-lookup`, {
        booking_number: booking.trim(),
        phone: phone.trim(),
      });
      setData(res.data);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  // Auto-trigger lookup when arriving with URL params (QR or post-payment redirect)
  useEffect(() => {
    if (searchParams.get("b") && searchParams.get("p")) {
      const t = setTimeout(() => submit(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll payment status when arriving with ?session_id=
  useEffect(() => {
    const sid = searchParams.get("session_id");
    if (!sid) {
      if (searchParams.get("payment") === "cancelled") {
        const t2 = setTimeout(() => setPayMsg(t("Pago cancelado. Puedes intentarlo otra vez.")), 0);
        return () => clearTimeout(t2);
      }
      return undefined;
    }
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 40; // ~2 minutes total
    setTimeout(() => setPayMsg(t("Verificando pago…")), 0);
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await axios.get(`${API}/public/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          setPayMsg(t("¡Pago recibido! Gracias."));
          submit();
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          return;
        }
        if (r.data.status === "expired") {
          setPayMsg(t("La sesión de pago ha expirado."));
          return;
        }
        if (attempts < MAX_ATTEMPTS) setTimeout(poll, 3000);
        else {
          setPayMsg(t("Estamos confirmando tu pago. Refresca la página en unos minutos para ver el estado actualizado."));
          submit();
        }
      } catch {
        if (attempts < MAX_ATTEMPTS) setTimeout(poll, 3000);
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPay = async () => {
    setPayMsg("");
    setPayLoading(true);
    try {
      const r = await axios.post(`${API}/public/services/checkout`, {
        booking_number: booking.trim(),
        phone: phone.trim(),
        origin_url: window.location.origin,
      });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e2) {
      setPayMsg(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setPayLoading(false);
    }
  };

  const driverPhotoUrl =
    data?.driver?.has_photo && data?.driver?.photo_token
      ? `${API}/public/drivers/${data.driver.id}/photo?token=${encodeURIComponent(
          data.driver.photo_token
        )}`
      : null;

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-black px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="DS Travel Tours" className="w-10 h-10 object-cover" />
          <div>
            <div className="font-display text-base font-bold">DS Travel Tours</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              {t("Consulta de reserva")}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <LanguageSwitcher />
          <Link
            to="/login"
            className="text-xs uppercase tracking-widest text-zinc-600 hover:text-black inline-flex items-center gap-2"
            data-testid="back-login"
          >
            <ArrowLeft size={14} /> {t("Acceso interno")}
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="client-lookup-page">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
          {t("Consulta tu servicio")}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-6">
          {t("¿Quién te recoge?")}
        </h1>

        <form
          onSubmit={submit}
          className="border-2 border-black p-6 bg-white grid grid-cols-1 sm:grid-cols-2 gap-4"
          data-testid="lookup-form"
        >
          <div>
            <Label className="text-xs uppercase tracking-widest">{t("Número de reserva")}</Label>
            <Input
              value={booking}
              onChange={(e) => setBooking(e.target.value)}
              placeholder={t("Ej. RSV-1001")}
              required
              className="mt-2 h-11 rounded-none border-black"
              data-testid="lookup-booking-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">{t("Teléfono")}</Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t("Ej. 611 22 33 44")}
              required
              className="mt-2 h-11 rounded-none border-black"
              data-testid="lookup-phone-input"
            />
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <Button
              type="submit"
              disabled={loading}
              className="rounded-none btn-primary h-11 px-6 text-xs uppercase tracking-widest"
              data-testid="lookup-submit-btn"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <>
                  <Search size={14} className="mr-2" /> {t("Buscar mi reserva")}
                </>
              )}
            </Button>
          </div>
          {err && (
            <div
              className="sm:col-span-2 text-sm text-red-700 bg-red-50 border border-red-300 px-3 py-2"
              data-testid="lookup-error"
            >
              {err}
            </div>
          )}
        </form>

        {data && (
          <section className="mt-8 animate-fade-up" data-testid="lookup-result">
            {/* Payment banner */}
            {payMsg && (
              <div className="mb-4 border-2 border-black bg-yellow-50 px-4 py-3 text-sm font-medium" data-testid="payment-msg">
                {payMsg}
              </div>
            )}
            {data.price != null && data.client_pays !== false && (
              <div className="mb-4 border-2 border-black bg-white p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="payment-block">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                    {t("Importe del servicio")}
                  </div>
                  <div className="font-display text-3xl font-bold">
                    {Number(data.price).toFixed(2)} €
                  </div>
                  <div className="text-xs uppercase tracking-widest mt-1 inline-flex items-center gap-1">
                    {data.payment_status === "paid" ? (
                      <span className="text-emerald-700 inline-flex items-center gap-1">
                        <CheckCircle2 size={14} /> {t("Pagado")}
                      </span>
                    ) : (
                      <span className="text-[#E63946]">{t("Pendiente de pago")}</span>
                    )}
                  </div>
                </div>
                {data.payment_status !== "paid" && (
                  <Button
                    onClick={onPay}
                    disabled={payLoading}
                    className="rounded-none btn-primary h-12 px-6 text-xs uppercase tracking-widest"
                    data-testid="pay-now-btn"
                  >
                    {payLoading ? (
                      <Loader2 className="animate-spin" size={16} />
                    ) : (
                      <>
                        <CreditCard size={14} className="mr-2" /> {t("Pagar ahora")}
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
            <div className="border border-black bg-white">
              <div className="bg-black text-white px-6 py-4 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">
                    {t("Reserva")}
                  </div>
                  <div className="font-display text-2xl font-bold font-mono">
                    {data.booking_number}
                  </div>
                  {data.provider && data.provider !== "ds_travel_tours" && (
                    <div
                      className="mt-2 inline-flex items-center gap-1.5 bg-[#F5B301] text-black text-[10px] uppercase tracking-widest font-bold px-2 py-1"
                      data-testid="provider-badge"
                    >
                      {t("Reservado vía")} {data.provider_label}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">{t("Estado")}</div>
                  <div className="font-display text-lg font-bold">{data.status_label}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-zinc-200">
                <div className="p-6 space-y-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                      {t("Cliente")}
                    </div>
                    <div className="font-display text-xl font-semibold">{data.client_name}</div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CalendarClock size={16} className="mt-1 text-zinc-500" />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {t("Fecha / Hora")}
                      </div>
                      <div className="font-semibold">
                        {data.pickup_date} · {data.pickup_time}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin size={16} className="mt-1 text-zinc-500" />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {t("Recogida")}
                      </div>
                      <div className="font-semibold">{data.pickup_location}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin size={16} className="mt-1 text-zinc-500" />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {t("Destino")}
                      </div>
                      <div className="font-semibold">{data.dropoff_location}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <UsersIcon size={14} className="text-zinc-500" />
                      <span className="text-sm">{data.passengers} {t("pasajero(s)")}</span>
                    </div>
                    {data.flight_number && (
                      <div className="flex items-center gap-2">
                        <Plane size={14} className="text-zinc-500" />
                        <span className="text-sm">{t("Vuelo ")}{data.flight_number}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-6 bg-zinc-50">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
                    {t("Tu conductor")}
                  </div>
                  {data.driver ? (
                    <div className="flex items-center gap-4">
                      <div className="w-24 h-24 border-2 border-black overflow-hidden bg-zinc-200 shrink-0 flex items-center justify-center">
                        {driverPhotoUrl ? (
                          <img
                            src={driverPhotoUrl}
                            alt={data.driver.name}
                            className="w-full h-full object-cover"
                            data-testid="driver-photo"
                          />
                        ) : (
                          <span className="text-3xl font-bold text-zinc-500">
                            {(data.driver.name || "?").slice(0, 1).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div>
                        <div className="font-display text-xl font-bold" data-testid="driver-name">
                          {data.driver.name}
                        </div>
                        {data.driver.phone && (
                          <a
                            href={`tel:${data.driver.phone}`}
                            className="inline-flex items-center gap-2 mt-2 text-sm underline underline-offset-4"
                            data-testid="driver-phone"
                          >
                            <Phone size={14} /> {data.driver.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-zinc-600">
                      {t("Aún no hemos asignado conductor. Te lo notificaremos en breve.")}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {data && <FeedbackForm booking={booking.trim()} phone={phone.trim()} t={t} />}

        <section
          className="mt-10 border border-black p-5 bg-zinc-50"
          data-testid="support-card"
        >
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
            {t("Atención al cliente")}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
            <a href="tel:+34624458803" className="flex items-center gap-3 text-sm font-semibold hover:underline">
              <span className="w-8 h-8 bg-black text-white flex items-center justify-center shrink-0">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              </span>
              +34 624 458 803
            </a>
            <a href="mailto:info@dstraveltoursllc.com" className="flex items-center gap-3 text-sm font-semibold hover:underline break-all">
              <span className="w-8 h-8 bg-black text-white flex items-center justify-center shrink-0">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </span>
              info@dstraveltoursllc.com
            </a>
          </div>
        </section>
      </main>
      <PublicAIChatWidget />
    </div>
  );
}

function FeedbackForm({ booking, phone, t = (s) => s }) {
  const [type, setType] = useState("invoice");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!message.trim()) {
      setErr(t("Por favor escribe un mensaje."));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/public/services/feedback`, {
        booking_number: booking,
        phone,
        request_type: type,
        message: message.trim(),
      });
      setSent(true);
      setMessage("");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <section className="mt-10 border-2 border-black p-6 bg-white" data-testid="feedback-sent">
        <div className="flex items-start gap-3">
          <CheckCircle2 size={22} className="text-emerald-600 shrink-0 mt-1" />
          <div>
            <div className="font-display text-lg font-bold">{t("Mensaje enviado")}</div>
            <p className="text-sm text-zinc-600 mt-1">
              {t("Nuestro centro de control se pondrá en contacto contigo en breve.")}
            </p>
            <button
              onClick={() => setSent(false)}
              className="mt-3 text-xs uppercase tracking-widest text-zinc-500 hover:text-black underline"
            >
              {t("Enviar otro mensaje")}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-10 border border-black bg-white" data-testid="feedback-form">
      <div className="border-b border-black p-4 font-display font-semibold text-sm uppercase tracking-widest">
        {t("¿Necesitas algo?")}
      </div>
      <form onSubmit={submit} className="p-5 space-y-4">
        <p className="text-sm text-zinc-600">
          {t("Solicita una")} <b>{t("factura")}</b>{t(", un")} <b>{t("cambio de hora")}</b> {t("o cuéntanos cualquier otra consulta.")}
        </p>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{t("Motivo")}</div>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-11 rounded-none border-black" data-testid="feedback-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="invoice">{t("Solicitar factura")}</SelectItem>
              <SelectItem value="time_change">{t("Cambio de hora")}</SelectItem>
              <SelectItem value="other">{t("Otra consulta")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{t("Mensaje")}</div>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="rounded-none border-black min-h-[100px]"
            placeholder={t("Cuéntanos con detalle qué necesitas…")}
            required
            data-testid="feedback-message"
          />
        </div>
        {err && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-300 px-3 py-2">{err}</div>
        )}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={sending}
            className="rounded-none btn-primary h-11 px-6 text-xs uppercase tracking-widest"
            data-testid="feedback-submit-btn"
          >
            {sending ? <Loader2 className="animate-spin" size={14} /> : (
              <><Send size={14} className="mr-2" /> {t("Enviar")}</>
            )}
          </Button>
        </div>
      </form>
    </section>
  );
}
